import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Users, Clock, Calendar, AlertTriangle, Building2, FileText, Loader2, CreditCard, ChevronDown, ChevronUp, Search } from 'lucide-react';
import { leaveApi, userApi, calendarApi, payslipApi, balanceApi } from '../../services/api';

interface LeaveApplication {
  applicationId: string;
  employeeId: string;
  employeeName: string;
  department?: string;
  departmentName?: string;
  leaveType: string;
  startDate: string;
  endDate: string;
  totalDays: number;
  status: string;
  supervisorRecommendation?: string;
  supervisorName?: string;
}

interface User {
  userId: string;
  firstName: string;
  lastName: string;
  department: string;
  status: string;
}

interface DepartmentSummary {
  name: string;
  onLeave: number;
  pending: number;
}

interface PayslipStatus {
  totalEmployees: number;
  uploadedCount: number;
  pendingCount: number;
  completionPercentage: number;
}

interface EmployeeBalance {
  userId: string;
  name: string;
  department: string;
  balances: {
    ANNUAL?: { available: number; total: number; taken: number };
    SICK?: { available: number; total: number; taken: number };
    FAMILY?: { available: number; total: number; taken: number };
    UNPAID?: { available: number; total: number; taken: number };
    SPECIAL?: { available: number; total: number; taken: number };
  };
}

export default function DirectorDashboard() {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [pendingApprovals, setPendingApprovals] = useState<LeaveApplication[]>([]);
  const [orgStats, setOrgStats] = useState({
    totalEmployees: 0,
    onLeaveToday: 0,
    pendingApprovals: 0,
    pendingCancellations: 0,
  });
  const [departmentSummary, setDepartmentSummary] = useState<DepartmentSummary[]>([]);
  const [payslipStatus, setPayslipStatus] = useState<PayslipStatus | null>(null);
  const [alerts, setAlerts] = useState<{ type: 'warning' | 'info'; message: string }[]>([]);
  const [employeeBalances, setEmployeeBalances] = useState<EmployeeBalance[]>([]);
  const [balancesExpanded, setBalancesExpanded] = useState(true);
  const [balanceSearch, setBalanceSearch] = useState('');

  useEffect(() => {
    const fetchDashboardData = async () => {
      setIsLoading(true);
      setError('');

      try {
        const today = new Date();
        const todayStr = today.toISOString().split('T')[0];
        const currentYear = today.getFullYear();
        const currentMonth = today.getMonth() + 1;

        // Fetch data in parallel
        const [
          pendingResponse,
          cancellationResponse,
          usersResponse,
          todayEventsResponse,
          payslipStatusResponse,
          balancesResponse,
        ] = await Promise.all([
          leaveApi.listApplications({ pendingFor: 'director' }).catch(() => ({ applications: [] })),
          leaveApi.listApplications({ status: 'PENDING_CANCELLATION' }).catch(() => ({ applications: [] })),
          userApi.listUsers({ activeOnly: true }).catch(() => ({ users: [] })),
          calendarApi.getEvents({ startDate: todayStr, endDate: todayStr, status: 'APPROVED' }).catch(() => ({ events: [] })),
          payslipApi.getStatus(currentYear, currentMonth).catch(() => null),
          balanceApi.listAllBalances(currentYear).catch(() => ({ employees: [] })),
        ]);

        const pending = (pendingResponse as { applications: LeaveApplication[] }).applications || [];
        const cancellations = (cancellationResponse as { applications: LeaveApplication[] }).applications || [];
        const users = (usersResponse as { users: User[] }).users || [];
        const todayEvents = (todayEventsResponse as { events: LeaveApplication[] }).events || [];

        setPendingApprovals(pending);

        // Calculate org stats
        setOrgStats({
          totalEmployees: users.length,
          onLeaveToday: todayEvents.length,
          pendingApprovals: pending.length,
          pendingCancellations: cancellations.length,
        });

        // Calculate department summary
        const deptMap = new Map<string, { onLeave: number; pending: number }>();

        // Initialize departments from users
        users.forEach(user => {
          if (user.department && !deptMap.has(user.department)) {
            deptMap.set(user.department, { onLeave: 0, pending: 0 });
          }
        });

        // Count people on leave today by department
        todayEvents.forEach(event => {
          const deptName = event.departmentName || event.department;
          if (deptName && deptMap.has(deptName)) {
            const dept = deptMap.get(deptName)!;
            dept.onLeave += 1;
          }
        });

        // Count pending by department
        pending.forEach(app => {
          const deptName = app.departmentName || app.department;
          if (deptName && deptMap.has(deptName)) {
            const dept = deptMap.get(deptName)!;
            dept.pending += 1;
          }
        });

        const deptSummary: DepartmentSummary[] = Array.from(deptMap.entries())
          .map(([name, stats]) => ({ name, ...stats }))
          .sort((a, b) => a.name.localeCompare(b.name));

        setDepartmentSummary(deptSummary);

        // Set payslip status
        if (payslipStatusResponse) {
          setPayslipStatus(payslipStatusResponse.summary);
        }

        // Set employee balances
        if (balancesResponse) {
          setEmployeeBalances((balancesResponse as { employees: EmployeeBalance[] }).employees || []);
        }

        // Generate alerts
        const newAlerts: { type: 'warning' | 'info'; message: string }[] = [];

        if (pending.length > 5) {
          newAlerts.push({
            type: 'warning',
            message: `${pending.length} leave applications are awaiting your approval`,
          });
        }

        if (cancellations.length > 0) {
          newAlerts.push({
            type: 'warning',
            message: `${cancellations.length} cancellation request${cancellations.length > 1 ? 's' : ''} pending approval`,
          });
        }

        // Check payslip status for current month
        if (payslipStatusResponse && payslipStatusResponse.summary.pendingCount > 0) {
          const monthName = new Date().toLocaleString('default', { month: 'long' });
          newAlerts.push({
            type: 'info',
            message: `${payslipStatusResponse.summary.pendingCount} payslip${payslipStatusResponse.summary.pendingCount > 1 ? 's' : ''} not yet uploaded for ${monthName}`,
          });
        }

        setAlerts(newAlerts);
      } catch (err: any) {
        console.error('Error fetching dashboard data:', err);
        setError(err.message || 'Failed to load dashboard data');
      } finally {
        setIsLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const formatLeaveType = (type: string) => {
    return type.charAt(0) + type.slice(1).toLowerCase().replace(/_/g, ' ');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
        <span className="ml-2 text-gray-600">Loading dashboard...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Director Dashboard</h1>
        <p className="text-sm text-gray-600">Organization-wide leave management</p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="rounded-md bg-red-50 p-4">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-5">
        <div className="card p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Total Employees</p>
              <p className="mt-1 text-3xl font-bold text-gray-900">{orgStats.totalEmployees}</p>
            </div>
            <div className="rounded-full bg-blue-100 p-3">
              <Users className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">On Leave Today</p>
              <p className="mt-1 text-3xl font-bold text-gray-900">{orgStats.onLeaveToday}</p>
            </div>
            <div className="rounded-full bg-green-100 p-3">
              <Calendar className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Pending Approvals</p>
              <p className="mt-1 text-3xl font-bold text-yellow-600">{orgStats.pendingApprovals}</p>
            </div>
            <div className="rounded-full bg-yellow-100 p-3">
              <Clock className="h-6 w-6 text-yellow-600" />
            </div>
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Cancellations</p>
              <p className="mt-1 text-3xl font-bold text-gray-900">{orgStats.pendingCancellations}</p>
            </div>
            <div className="rounded-full bg-red-100 p-3">
              <AlertTriangle className="h-6 w-6 text-red-600" />
            </div>
          </div>
        </div>

        {payslipStatus && (
          <Link to="/admin/payslips" className="card p-6 transition-colors hover:bg-gray-50">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Payslips</p>
                <p className="mt-1 text-3xl font-bold text-gray-900">
                  {payslipStatus.completionPercentage}%
                </p>
                <p className="text-xs text-gray-400">
                  {payslipStatus.uploadedCount}/{payslipStatus.totalEmployees} uploaded
                </p>
              </div>
              <div className="rounded-full bg-purple-100 p-3">
                <CreditCard className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </Link>
        )}
      </div>

      {/* Alerts */}
      {alerts.length > 0 && (
        <div className="space-y-2">
          {alerts.map((alert, index) => (
            <div
              key={index}
              className={`flex items-center gap-3 rounded-lg p-4 ${
                alert.type === 'warning'
                  ? 'bg-yellow-50 text-yellow-800'
                  : 'bg-blue-50 text-blue-800'
              }`}
            >
              <AlertTriangle className="h-5 w-5 flex-shrink-0" />
              <p className="text-sm">{alert.message}</p>
            </div>
          ))}
        </div>
      )}

      {/* Pending Approvals */}
      <div className="card">
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <h2 className="text-lg font-semibold text-gray-900">Pending Your Approval</h2>
          {pendingApprovals.length > 0 && (
            <span className="inline-flex items-center rounded-full bg-yellow-100 px-2.5 py-0.5 text-xs font-medium text-yellow-800">
              {pendingApprovals.length} pending
            </span>
          )}
        </div>
        <div className="p-6">
          {pendingApprovals.length === 0 ? (
            <p className="text-center text-gray-500">No pending approvals</p>
          ) : (
            <div className="space-y-4">
              {pendingApprovals.map(request => (
                <div
                  key={request.applicationId}
                  className="flex items-center justify-between rounded-lg border border-yellow-200 bg-yellow-50 p-4"
                >
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-gray-900">{request.employeeName}</p>
                      <span className="text-xs text-gray-500">({request.departmentName || request.department || 'No department'})</span>
                    </div>
                    <p className="text-sm text-gray-600">
                      {formatLeaveType(request.leaveType)}: {request.startDate} - {request.endDate} ({request.totalDays} day{request.totalDays !== 1 ? 's' : ''})
                    </p>
                    {request.supervisorName && (
                      <p className="text-xs text-gray-500">
                        Supervisor: {request.supervisorName}
                        {request.supervisorRecommendation === 'RECOMMEND' && (
                          <span className="ml-2 text-green-600">Recommended</span>
                        )}
                      </p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Link
                      to={`/leave/${request.applicationId}`}
                      className="btn-primary text-sm"
                    >
                      Review
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Department Summary */}
      <div className="card">
        <div className="border-b border-gray-200 px-6 py-4">
          <h2 className="text-lg font-semibold text-gray-900">Department Overview</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Department
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500">
                  On Leave Today
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500">
                  Pending Requests
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {departmentSummary.map(dept => (
                <tr key={dept.name} className="hover:bg-gray-50">
                  <td className="whitespace-nowrap px-6 py-4 text-sm font-medium text-gray-900">
                    {dept.name}
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-center text-sm text-gray-500">
                    {dept.onLeave > 0 ? (
                      <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
                        {dept.onLeave}
                      </span>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-center text-sm text-gray-500">
                    {dept.pending > 0 ? (
                      <span className="inline-flex items-center rounded-full bg-yellow-100 px-2.5 py-0.5 text-xs font-medium text-yellow-800">
                        {dept.pending}
                      </span>
                    ) : (
                      '-'
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Employee Leave Balances */}
      <div className="card">
        <div
          className="flex cursor-pointer items-center justify-between border-b border-gray-200 px-6 py-4"
          onClick={() => setBalancesExpanded(!balancesExpanded)}
        >
          <h2 className="text-lg font-semibold text-gray-900">Employee Leave Balances</h2>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">{employeeBalances.length} employees</span>
            {balancesExpanded ? (
              <ChevronUp className="h-5 w-5 text-gray-500" />
            ) : (
              <ChevronDown className="h-5 w-5 text-gray-500" />
            )}
          </div>
        </div>

        {balancesExpanded && (
          <>
            {/* Search */}
            <div className="border-b border-gray-200 px-6 py-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by name or department..."
                  value={balanceSearch}
                  onChange={e => setBalanceSearch(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 py-2 pl-10 pr-4 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
              </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Employee
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Department
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500">
                      Annual
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500">
                      Sick
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500">
                      Family
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500">
                      Unpaid
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500">
                      Special
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {employeeBalances
                    .filter(emp => {
                      if (!balanceSearch) return true;
                      const search = balanceSearch.toLowerCase();
                      return (
                        emp.name.toLowerCase().includes(search) ||
                        emp.department.toLowerCase().includes(search)
                      );
                    })
                    .map(emp => (
                      <tr key={emp.userId} className="hover:bg-gray-50">
                        <td className="whitespace-nowrap px-6 py-4 text-sm font-medium text-gray-900">
                          {emp.name}
                        </td>
                        <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                          {emp.department}
                        </td>
                        <td className="whitespace-nowrap px-4 py-4 text-center text-sm">
                          {emp.balances.ANNUAL ? (
                            <span
                              className={`inline-flex min-w-[3rem] justify-center rounded-full px-2 py-0.5 text-xs font-medium ${
                                emp.balances.ANNUAL.available < 3
                                  ? 'bg-red-100 text-red-800'
                                  : emp.balances.ANNUAL.available < 5
                                  ? 'bg-yellow-100 text-yellow-800'
                                  : 'bg-green-100 text-green-800'
                              }`}
                            >
                              {emp.balances.ANNUAL.available}/{emp.balances.ANNUAL.total}
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-4 py-4 text-center text-sm">
                          {emp.balances.SICK ? (
                            <span
                              className={`inline-flex min-w-[3rem] justify-center rounded-full px-2 py-0.5 text-xs font-medium ${
                                emp.balances.SICK.available < 3
                                  ? 'bg-red-100 text-red-800'
                                  : 'bg-blue-100 text-blue-800'
                              }`}
                            >
                              {emp.balances.SICK.available}/{emp.balances.SICK.total}
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-4 py-4 text-center text-sm">
                          {emp.balances.FAMILY ? (
                            <span className="inline-flex min-w-[3rem] justify-center rounded-full bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-800">
                              {emp.balances.FAMILY.available}/{emp.balances.FAMILY.total}
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-4 py-4 text-center text-sm">
                          {emp.balances.UNPAID ? (
                            <span className="inline-flex min-w-[3rem] justify-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-800">
                              {emp.balances.UNPAID.taken}
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-4 py-4 text-center text-sm">
                          {emp.balances.SPECIAL ? (
                            <span className="inline-flex min-w-[3rem] justify-center rounded-full bg-orange-100 px-2 py-0.5 text-xs font-medium text-orange-800">
                              {emp.balances.SPECIAL.taken}
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
              {employeeBalances.length === 0 && (
                <div className="py-8 text-center text-gray-500">No employee balances found</div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Quick Actions */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Link to="/calendar" className="card p-6 transition-colors hover:bg-gray-50">
          <div className="flex items-center gap-4">
            <div className="rounded-full bg-blue-100 p-3">
              <Calendar className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <p className="font-medium text-gray-900">Organization Calendar</p>
              <p className="text-sm text-gray-600">View all leave schedules</p>
            </div>
          </div>
        </Link>
        <Link to="/reports" className="card p-6 transition-colors hover:bg-gray-50">
          <div className="flex items-center gap-4">
            <div className="rounded-full bg-purple-100 p-3">
              <FileText className="h-6 w-6 text-purple-600" />
            </div>
            <div>
              <p className="font-medium text-gray-900">Reports</p>
              <p className="text-sm text-gray-600">Leave usage and analytics</p>
            </div>
          </div>
        </Link>
        <Link to="/admin/users" className="card p-6 transition-colors hover:bg-gray-50">
          <div className="flex items-center gap-4">
            <div className="rounded-full bg-green-100 p-3">
              <Building2 className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <p className="font-medium text-gray-900">User Management</p>
              <p className="text-sm text-gray-600">Manage employees and roles</p>
            </div>
          </div>
        </Link>
        <Link to="/admin/holidays" className="card p-6 transition-colors hover:bg-gray-50">
          <div className="flex items-center gap-4">
            <div className="rounded-full bg-red-100 p-3">
              <Calendar className="h-6 w-6 text-red-600" />
            </div>
            <div>
              <p className="font-medium text-gray-900">Holiday Management</p>
              <p className="text-sm text-gray-600">Manage public holidays</p>
            </div>
          </div>
        </Link>
        <Link to="/admin/payslips" className="card p-6 transition-colors hover:bg-gray-50">
          <div className="flex items-center gap-4">
            <div className="rounded-full bg-purple-100 p-3">
              <CreditCard className="h-6 w-6 text-purple-600" />
            </div>
            <div>
              <p className="font-medium text-gray-900">Payslip Management</p>
              <p className="text-sm text-gray-600">Upload employee payslips</p>
            </div>
          </div>
        </Link>
      </div>
    </div>
  );
}
